import { inject, observer } from 'mobx-react'
import cx from 'classnames'
import { Main } from 'layouts'
import { GetStaticProps } from 'next'
import rootStore, { RootStore } from 'stores/index'
import styles from 'pagestyles/accessories.module.scss'
import { PriceManager } from 'prices/priceManager'
import React, { FC, useRef } from 'react'
import { LinkWithArrow } from 'components/basic/LinkWithArrow'
import { Heading } from 'components/basic/Heading'
import { useRouter } from 'next/router'
import { Img } from 'components/basic/Img'
import { AdtPixel } from 'components/_pixels'
import { PodCompareDualColumnCTAProps } from 'components/PodCompareDualColumnCTA'
import dynamic from 'next/dynamic'
import gsap from 'gsap'
import { useGSAP } from '@gsap/react'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'
import { AccessoryCard } from 'components/AccessoryCard'
import { PodUpsell } from 'components/PodUpsell'

const PodCompareDualColumnCTALazy = dynamic<PodCompareDualColumnCTAProps>(() => import('components/PodCompareDualColumnCTA').then((module) => module.PodCompareDualColumnCTA), {
	ssr: false,
})

interface Props {
	rootStore: RootStore
}

const Accessories: FC<Props> = (props) => {
	const { rootStore } = props
	const data = rootStore.productStore.getAccessoriesProducts('accessories')

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const x = Object.keys(rootStore.priceStore.allPrices).length // forces rerender do not delete

	const prices = rootStore.priceStore.allPrices
	const settings = rootStore.settingsStore.settings
	const isSale = settings && settings.saleName

	const router = useRouter()

	const bannerContainerRef = useRef<HTMLDivElement>(null)
	const accessoriesCardsContainerRef = useRef<HTMLDivElement>(null)

	useGSAP(() => {
		gsap.registerPlugin(ScrollTrigger)

		gsap.to(bannerContainerRef.current, {
			ease: 'none',
			clipPath: 'inset(6.25% round 25px)',
			scrollTrigger: {
				trigger: bannerContainerRef.current,
				endTrigger: accessoriesCardsContainerRef.current,
				start: 'top top+=200px',
				end: 'top center',
				scrub: 1,
			},
		})
	})

	data.forEach((section) => {
		section.items.forEach((product) => {
			if (!Object.keys(prices).length) {
				product.prices = PriceManager.formatPrices(PriceManager.emptyPrice, 'USD')
			} else {
				product.prices = PriceManager.formatPrices(prices[product.variantId].prices, 'USD')
			}
		})
	})

	function switchToUS() {
		props.rootStore.settingsStore.setRegion('')
		router.push(`/accessories`).then()
	}

	const region = rootStore.settingsStore.currentRegion

	let country: string
	switch (region) {
		case 'au':
			country = 'Australia'
			break
		case 'uk':
			country = 'the UK'
			break
		case 'eu':
			country = 'Europe'
			break
		case 'ca':
			country = 'Canada'
			break
		default:
			country = 'your country'
	}

	if (region !== '') {
		const timestamp = Date.now().toString()
		return (
			<Main>
				<div className={styles.no_accessories_wrapper}>
					<div className={styles.no_accessories_container}>
						<div className={styles.no_accessories_message}>
							<Img
								src="https://eightsleep.imgix.net/warning.svg?v=1645636991"
								alt="warning"
							/>
							<Heading
								type={'h5'}
								title={`Unfortunately accessories do not ship to ${country} at this time`}
								titleClass={styles.no_accessories_heading}
							/>
						</div>
						<div className={styles.no_accessories_arrow_link}>
							<LinkWithArrow onClick={switchToUS}>{"Click here if you're shipping to the US"}</LinkWithArrow>
						</div>
					</div>
				</div>
				<section
					key={`section-wrapper-${timestamp}`}
					className={cx(styles.dual_cta_container, 'inner-paddings-y test')}
				>
					<PodCompareDualColumnCTALazy
						rootStore={props.rootStore}
						className={styles.dual_cta}
						title={<>{isSale ? `Save with our ${settings.saleName}` : ''}</>}
						paragraphs={["Shop risk-free with our free home trial options. Don't love it? We'll arrange a pick-up and issue a full refund. No questions asked. "]}
						buttonContent="Shop all"
						src="https://eightsleep.imgix.net/bottom-cta-01.png?v=1642116714"
					/>
				</section>
			</Main>
		)
	}

	return (
		<Main>
			<section className={styles.accessories}>
				<div className={styles.headline_container}>
					<div className={styles.subtitle}>Accessories</div>
					<div className={styles.title}>
						Create the perfect
						<br />
						sleep setup
					</div>
				</div>

				<div
					className={styles.banner_container}
					ref={bannerContainerRef}
				>
					<Img
						sources={[
							{
								mediaQuery: '(max-width: 599px)',
								src: 'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/accessories_desktop_hero.png',
							},
						]}
						src="https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/accessories_desktop_hero.png"
						alt="Banner image"
						objectFit="cover"
						className={styles.cta_image}
					/>
				</div>

				<div className={cx(styles.enhance_text_card, styles.mobile)}>Enhance your experience with premium accessories</div>
				<div
					className={cx(styles.grid_container, styles.accessories_cards_container)}
					ref={accessoriesCardsContainerRef}
				>
					<div className={cx(styles.enhance_text_card, styles.desktop)}>Enhance your experience with premium accessories</div>
					{data
						.flatMap((section) => section.items)
						.map((item, index) => (
							<AccessoryCard
								key={index}
								{...item}
							/>
						))}
				</div>
			</section>

			<PodUpsell />

			<AdtPixel id="6570" />
		</Main>
	)
}

export const getStaticProps: GetStaticProps = async () => {
	const data = rootStore.productStore.productManager.getPageProducts('accessories', '', rootStore.settingsStore.settings.saleName)
	return { props: { data } }
}

export default inject('rootStore')(observer(Accessories))
